/**
 * Header superior sticky.
 *
 * Estructura (de izquierda a derecha):
 *   - branding del tenant (logo + texto logo_alt_text) o icono superuser
 *   - bloque del usuario (avatar + nombre centrado) con menu desplegable
 */
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@/components/shared/overlays/Tooltip';
import {
  DropdownMenu,
  DropdownMenuDivider,
  DropdownMenuItem,
} from '@/components/shared/overlays/DropdownMenu';
import { useAuth } from '@/contexts/AuthContext';
import { useLogout } from '@/services/api/useAuth';
import { useTenantLogo } from '@/hooks/useTenantLogo';
import { TenantContextSelector } from '@/components/shared/layout/TenantContextSelector';
import type { ReactNode } from 'react';

type HeaderProps = {
  rightSlot?: ReactNode;
};

export function Header({ rightSlot }: HeaderProps) {
  const { user } = useAuth();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuLabelId = useId();
  const [menuOpen, setMenuOpen] = useState(false);

  const tenantId = user?.active_tenant_id ?? null;
  const tenantLogoUrl = user?.tenant_logo_url ?? null;
  const tenantName = user?.tenant_name ?? null;
  const tenantLogoAltRaw = user?.tenant_logo_alt_text ?? null;
  const tenantLogoAlt = tenantLogoAltRaw?.trim() ?? '';
  const { objectUrl: headerLogoUrl } = useTenantLogo(tenantId ?? undefined, tenantLogoUrl);
  const mustRotate = user?.credential_must_rotate ?? false;

  /**
   * Publica el alto real del aviso para que las páginas resten el cromo que
   * corresponde.
   *
   * El aviso es `flex-wrap`, así que su alto no es una constante: en móvil el
   * texto y el botón se apilan y ocupa el doble. Medirlo con `ResizeObserver`
   * deja un único lugar que conoce el valor y evita el `calc` repartido por 20
   * pantallas, que es lo que dejó la última fila de las tablas cortada.
   *
   * `useLayoutEffect` evita el parpadeo: se mide antes de pintar, no después.
   * La limpieza devuelve `0px` para que el valor no sobreviva al desmontaje.
   */
  const bannerRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!banner) {
      document.documentElement.style.setProperty('--app-credential-banner-height', '0px');
      return undefined;
    }
    const publish = () => {
      document.documentElement.style.setProperty(
        '--app-credential-banner-height',
        `${banner.offsetHeight}px`,
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(banner);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--app-credential-banner-height', '0px');
    };
  }, [mustRotate]);

  const hasLogo = !!headerLogoUrl && !!tenantId;
  const brandLabel = tenantName ?? '';
  const showLogo = hasLogo;
  const showText = !!tenantName;

  const handleToggle = () => setMenuOpen((v) => !v);
  const handleClose = () => setMenuOpen(false);
  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && !menuOpen) {
      e.preventDefault();
      setMenuOpen(true);
    }
  };

  return (
    <>
      {/*
        Aviso de credencial temporal. Va fuera del `<header>` para poder ocupar
        su propio ancho: es una franja de la aplicación, no una pieza del
        encabezado. No bloquea la navegación porque el backend ya bloquea las
        escrituras; informa y ofrece la salida.

        Los colores son deliberadamente oscuros (`text-amber-900` sobre ámbar
        claro) en vez del token `warning`: el token ronda 2.5:1 de contraste y
        este es el aviso central del estado `temporal` de §16.8. Con el token,
        el texto resultaba ilegible.
      */}
      {mustRotate && (
        <aside
          ref={bannerRef}
          aria-label="Aviso de credencial temporal"
          className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2.5"
        >
          <p className="flex items-center gap-2 text-xs font-medium text-amber-900">
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">lock_clock</span>
            Tu contraseña es temporal. Puedes leer, pero no operar hasta establecer una definitiva.
          </p>
          <button
            type="button"
            onClick={() => navigate('/change-password')}
            className="shrink-0 rounded-md bg-amber-800 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            Establecer contraseña
          </button>
        </aside>
      )}
      <header
        role="banner"
        className="sticky top-[var(--app-credential-banner-height,0px)] z-40 flex h-[60px] items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm"
      >
      <div className="flex items-center gap-2 lg:ml-0 ml-12 min-w-0">
         {user?.has_platform_access && !tenantId ? (

          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-light border border-primary/20">
              <span className="material-symbols-outlined text-[20px] text-primary">shield_person</span>
            </div>
            <span className="hidden sm:inline text-sm font-semibold text-slate-800 truncate max-w-[180px]">Administración Global</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            {showLogo && (
              <div
                role="img"
                className="flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white p-1 shrink-0"
                aria-label={brandLabel ? `Logo de ${brandLabel}` : tenantName ? `Logo de ${tenantName}` : 'Logo del tenant'}
              >
                <img
                  src={headerLogoUrl!}
                  alt={tenantLogoAlt || tenantName || 'Logo del tenant'}
                  className="h-full w-full object-contain"
                />
              </div>
            )}
            {!showLogo && (
              <div className="hidden sm:flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 bg-slate-50 shrink-0">
                <span className="material-symbols-outlined text-[22px] text-slate-400">apartment</span>
              </div>
            )}
            {showText && brandLabel && (
              <Tooltip title={brandLabel} placement="bottom">
                <span tabIndex={0} className="hidden sm:inline font-brand text-[15px] md:text-base font-semibold text-slate-800 truncate max-w-[220px] rounded outline-none focus-visible:ring-2 focus-visible:ring-primary/50">{brandLabel}</span>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {rightSlot}

        {user && <TenantContextSelector />}

        {user && (
          <>
            <button
              type="button"
              ref={triggerRef}
              id={menuLabelId}
              onClick={handleToggle}
              onKeyDown={handleTriggerKeyDown}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls="user-menu"
              aria-label="Menú de usuario"
              className="no-underline-link flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100 transition-colors"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white shrink-0">
                {user.full_name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-slate-900 leading-none">{user.full_name}</span>
              <span className="material-symbols-outlined text-[18px] text-slate-400 hidden sm:inline">expand_more</span>
            </button>
            <DropdownMenu
              id="user-menu"
              open={menuOpen}
              onClose={handleClose}
              anchorRef={triggerRef}
              placement="bottom-right"
              labelledBy={menuLabelId}
            >
              <DropdownMenuItem
                icon="person"
                onClick={() => {
                  handleClose();
                  navigate('/app/profile');
                }}
              >
                Mi perfil
              </DropdownMenuItem>
              <DropdownMenuDivider />
              <DropdownMenuItem
                icon="logout"
                onClick={() => {
                  handleClose();
                  logout();
                }}
              >
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenu>
          </>
        )}
      </div>
      </header>
    </>
  );
}
